import { CommonModule, DatePipe } from '@angular/common';
import { ChangeDetectorRef, Component, Inject, Input, ViewChild } from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import {
  MatDatepicker,
  MatDatepickerModule,
} from '@angular/material/datepicker';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { CommonService } from '../../../../../../services/common.service';
import { NgxSpinnerService, NgxSpinnerComponent } from 'ngx-spinner';
import { ToastrService } from '@openng/ngx-toastr';
import { MatTable, MatTableModule } from '@angular/material/table';
import { ContributorCommissionRatePlanService } from '../../../../../../services/contributor-commission-rate-plan.service';
import { CommissionRateTemplateService } from '../../../../../../services/commission-rate-template.service';
import {
  ContributorCommissionRatePlanApprovalDto,
  ContributorCommissionRatePlanUpdateDto,
  resolveDualCommissionRates,
} from '../../../../models/commission-rate-tier.models';

@Component({
  selector: 'app-manage-commission-rate-plan',
  standalone: true,
  imports: [
    MatDatepicker,
    MatDatepickerModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatIconModule,
    CommonModule,
    MatDialogModule,
    NgxSpinnerComponent,
    MatTableModule,
  ],
  providers: [NgxSpinnerService, DatePipe],
  templateUrl: './manage-commission-rate-plan-dialog.component.html',
  styleUrls: ['./manage-commission-rate-plan-dialog.component.scss'],
})
export class ManageCommissionRatePlanDialogComponent {
  plans: any;

  @Input() user: any; // Assuming this is passed from the parent component
  @Input() mode: 'add' | 'edit' | 'view' = 'add';
  @ViewChild(MatTable) table!: MatTable<any>;
  templateForm!: FormGroup;

  constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<ManageCommissionRatePlanDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private commonService: CommonService,
    private spinner: NgxSpinnerService,
    private toasterService: ToastrService,
    private cdr: ChangeDetectorRef,
    private commissionRatePlanService: ContributorCommissionRatePlanService,
    private commissionRateTemplateService: CommissionRateTemplateService,
    private datePipe: DatePipe
  ) { }

  incentiveForm!: FormGroup;
  thresholdFormArray!: FormArray;
  public templates: any;
  selectedContributionPeriodName = '';
  header: string = 'Add Commission Rate Plan';
  isReadOnly = false;
  existingEffectiveDates: string[] = [];

  thresholdDisplayedColumns: string[] = ['min', 'max', 'contractRate', 'permRate'];

  private toLocalDate(value: string | null | undefined): Date | null {
    if (!value) {
      return null;
    }

    return this.commonService.parseLocalDate(value);
  }

  ngOnInit(): void {
    this.mode = this.data?.mode || 'add';
    this.isReadOnly = this.mode === 'view';
    this.existingEffectiveDates = this.data?.existingEffectiveDates ?? [];

    if (this.mode === 'edit') {
      this.header = 'Edit Commission Rate Plan';
    } else if (this.mode === 'view') {
      this.header = 'View Commission Rate Plan';
      this.thresholdDisplayedColumns = ['min', 'max', 'contractRate', 'permRate'];
    }

    this.thresholdFormArray = this.fb.array([this.createThresholdRow()]);

    this.incentiveForm = this.fb.group({
      templateId: [null, Validators.required],
      effectiveDate: [{ value: new Date(), disabled: this.mode === 'edit' }],
      thresholds: this.thresholdFormArray,
    });

    if (this.mode === 'edit' || this.mode === 'view') {
      this.commissionRatePlanService
        .getContributorCommissionById(this.data.data.contributorId, this.data.data.id)
        .subscribe({
          next: (data) => {
            this.incentiveForm.patchValue({
              effectiveDate: this.toLocalDate(data.effectiveDate),
              templateId: data.templateId
            });

            this.loadThresholds(data.commissionTemplateTiers);

            this.loadTemplates(data.templateId);

            if (this.isReadOnly) {
              this.incentiveForm.disable({ emitEvent: false });
            }

            this.cdr.detectChanges();
          },
          error: () => {

            this.toasterService.error('Failed to load commission rate plan details');
            this.cdr.detectChanges();
          },
        });

    } else {
      this.commonService.getCommissionRateTemplates().subscribe({
        next: (response: any) => {
          if (!response || !Array.isArray(response)) {
            this.toasterService.error('Failed to load commission rate plans');

            return;
          }

          this.templates = response;

          const initialTemplateId = this.templates[0]?.templateId ?? null;

          if (initialTemplateId != null) {
            this.incentiveForm.patchValue({ templateId: initialTemplateId });
            this.onTemplateChange(initialTemplateId, true);
          }

          this.cdr.detectChanges();


        },
        error: () => {
          
          this.toasterService.error('Failed to load commission rate plans');
          this.cdr.detectChanges();
        }
      });
    }
  }


  loadTemplates(templateId: any) {
    this.commonService.getCommissionRateTemplates().subscribe({
      next: (response: any) => {
        if (response && Array.isArray(response)) {
          this.templates = response;

          const selectedTemplateId =
            response.find((t: any) => t.templateId === templateId)?.templateId ??
            response[0]?.templateId;

          if (selectedTemplateId != null) {
            this.incentiveForm.patchValue({ templateId: selectedTemplateId });

            this.onTemplateChange(selectedTemplateId, false);
          }

          if (this.isReadOnly) {
            this.incentiveForm.disable({ emitEvent: false });
          }
        } else {
          this.toasterService.error('Failed to load commission rate plans');
        }

        this.cdr.detectChanges();
      },
      error: () => {
        this.toasterService.error('Failed to load commission rate plans');
        this.cdr.detectChanges();
      }
    });
  }

  createThresholdRow(): FormGroup {
    return this.fb.group({
      min: [''],
      max: [''],
      contractRate: ['', [Validators.required, Validators.min(0), Validators.max(100)]],
      permRate: ['', [Validators.required, Validators.min(0), Validators.max(100)]],
    });
  }

  loadThresholds(rateDtos: any[]): void {
    const validRates = (rateDtos ?? [])
      .map((dto: any) => {
        const resolvedRates = resolveDualCommissionRates(dto)
        return {
          ...dto,
          contractRatePercentage: resolvedRates.contractRatePercentage,
          permRatePercentage: resolvedRates.permRatePercentage,
        }
      })
      .filter(dto =>
        dto.thresholdStart != null &&
        dto.thresholdEnd != null &&
        dto.contractRatePercentage != null &&
        dto.permRatePercentage != null &&
        (dto.thresholdStart !== 0 ||
          dto.thresholdEnd !== 0 ||
          dto.contractRatePercentage !== 0 ||
          dto.permRatePercentage !== 0)
      );

    this.thresholdFormArray.clear();

    if (!validRates.length) {
      this.thresholdFormArray.push(this.createThresholdRow());
      return;
    }

    validRates.forEach((dto) => {
      this.thresholdFormArray.push(
        this.fb.group({
          min: [{ value: dto.thresholdStart, disabled: true }, Validators.required],
          max: [{ value: dto.thresholdEnd, disabled: true }, Validators.required],
          contractRate: [{ value: dto.contractRatePercentage, disabled: true }, [Validators.required, Validators.min(0), Validators.max(100)]],
          permRate: [{ value: dto.permRatePercentage, disabled: true }, [Validators.required, Validators.min(0), Validators.max(100)]],
        })
      );
    });
  }

  submit(): void {
    if (this.isReadOnly) {
      this.dialogRef.close();
      return;
    }

    if (!this.hasValidThresholds()) {
      this.toasterService.error('At least one threshold row with valid values is required');
      return;
    }

    if (this.isDuplicateEffectiveDate()) {
      this.toasterService.error('A commission rate plan with this effective date already exists');
      return;
    }

    if (this.incentiveForm.valid || this.mode === 'edit') {
      const formValues = this.incentiveForm.getRawValue();
      const template = this.templates.find(
        (t: { templateId: any }) =>
          t.templateId === formValues.templateId
      );

      if (this.data.mode === 'edit') {
        const thresholdValues = this.thresholdFormArray.getRawValue();
        const dto: ContributorCommissionRatePlanUpdateDto = {
          id: this.data.data.id, // Include the ID for update
          name: template.templateName,
          contributorId: this.data.user.id,
          templateId: template.templateId,
          templateName: template.templateName,
          effectiveDate: this.datePipe.transform(
            this.incentiveForm.getRawValue().effectiveDate,
            'yyyy-MM-dd'
          ),
          commissionRates: thresholdValues.map(
            (threshold: any) => ({
              ytdThresholdStart: threshold.min,
              ytdThresholdEnd: threshold.max,
              contractRatePercentage: Number(threshold.contractRate),
              permRatePercentage: Number(threshold.permRate),
            })
          ),
        };

        this.commissionRatePlanService
          .updateContribuitorCommissionRatePlan(this.data.data.id, dto)
          .subscribe(
            (response: any) => {
              this.toasterService.success(
                'Commission Rate Plan submitted for approval successfully'
              );
              this.dialogRef.close(response);
            },
            (error: any) => {
              this.toasterService.error(
                'Failed to submit Commission Rate Plan for approval'
              );
              console.error('Error submitting Commission Rate Plan:', error);
            }
          );
      } else {
        const thresholdValues = this.thresholdFormArray.getRawValue();
        const dto: ContributorCommissionRatePlanApprovalDto = {
          name: template.templateName,
          contributorId: this.data.user.id,
          templateId: template.templateId,
          templateName: template.templateName,
          effectiveDate: this.datePipe.transform(
            this.incentiveForm.getRawValue().effectiveDate,
            'yyyy-MM-dd'
          ),
          commissionTemplateTiers: thresholdValues.map(
            (threshold: any) => ({
              thresholdStart: threshold.min,
              thresholdEnd: threshold.max,
              contractRatePercentage: Number(threshold.contractRate),
              permRatePercentage: Number(threshold.permRate),
            })
          ),
        };

        this.commissionRatePlanService
          .submitCommissionRatePlanForApproval(dto)
          .subscribe(
            (response: any) => {
              this.toasterService.success(
                'Commission Rate Plan submitted for approval successfully'
              );
              this.dialogRef.close(response);
            },
            (error: any) => {
              this.toasterService.error(
                error.error?.message || 'Failed to submit Commission Rate Plan for approval'
              );
            }
          );
      }
    }
  }

  cancel(): void {
    this.dialogRef.close();
  }

  hasValidThresholds(): boolean {
    const thresholds = this.thresholdFormArray.getRawValue();
    return thresholds.some(
      (t: any) =>
        t.min != null && t.min !== '' &&
        t.max != null && t.max !== '' &&
        t.contractRate != null && t.contractRate !== '' &&
        t.permRate != null && t.permRate !== ''
    );
  }

  isDuplicateEffectiveDate(): boolean {
    const effectiveDate = this.datePipe.transform(
      this.incentiveForm.getRawValue().effectiveDate,
      'yyyy-MM-dd'
    );
    if (!effectiveDate) return false;

    return this.existingEffectiveDates.some(existingDate => {
      const formattedExisting = this.datePipe.transform(existingDate, 'yyyy-MM-dd');
      return formattedExisting === effectiveDate;
    });
  }

  loadSelectedThresholds(event: any) {
    if (this.isReadOnly) return;

    const templateId = event?.value ?? event;
    if (!templateId) return;

    // Ensure FormArray exists (safety)
    if (!this.thresholdFormArray) {
      this.thresholdFormArray = this.fb.array([this.createThresholdRow()]);
      this.incentiveForm?.setControl('thresholds', this.thresholdFormArray);
    }

    this.incentiveForm.patchValue({ templateId });

    // Fetch full template details including rate tiers
    this.commissionRateTemplateService.getCommissionRateTemplatesById(templateId).subscribe({
      next: (templateDetails: any) => {
        const thresholds =
          templateDetails?.templateTiers ??
          templateDetails?.contributorCommissionRatePlanRateDtos ??
          templateDetails?.contributorCommissionRatePlanRateDto ??
          templateDetails?.commissionRateTemplateTiers ??
          [];

        // Rebuild rows in the EXISTING FormArray instance
        this.thresholdFormArray.clear();

        if (!thresholds.length) {
          this.thresholdFormArray.push(this.createThresholdRow());
          return;
        }

        thresholds.forEach((threshold: any, index: number) => {
          const resolvedRates = resolveDualCommissionRates(threshold)

          this.thresholdFormArray.push(
            this.fb.group({
              min: [{ value: threshold.ytdThresholdStart ?? threshold.thresholdStart ?? null, disabled: true }, Validators.required],
              max: [{ value: threshold.ytdThresholdEnd ?? threshold.thresholdEnd ?? null, disabled: true }, Validators.required],
              contractRate: [{ value: resolvedRates.contractRatePercentage, disabled: true }, [Validators.required, Validators.min(0), Validators.max(100)]],
              permRate: [{ value: resolvedRates.permRatePercentage, disabled: true }, [Validators.required, Validators.min(0), Validators.max(100)]],
            })
          );
        });

        // Trigger table refresh
        this.table?.renderRows();
        this.cdr.detectChanges();
      },
      error: () => {
        this.thresholdFormArray.clear();
        this.thresholdFormArray.push(this.createThresholdRow());
        this.table?.renderRows();
        this.cdr.detectChanges();
      }
    });
  }

  onTemplateChange(templateId: number, loadThresholds = true) {
    const t = this.templates?.find((x: any) => x.templateId === templateId);
    this.selectedContributionPeriodName = t?.contributionPeriodName ?? '';

    if (loadThresholds && !this.isReadOnly) {
      this.loadSelectedThresholds({ value: templateId } as any);
    }
  }


}
